/**
 * 活动数据准备工具 - Stateless 纯函数
 * 用于前端准备活动创建所需的数据
 */

/**
 * 验证活动名称
 * @param {string} activityName - 活动名称
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateActivityName(activityName) {
  if (!activityName || typeof activityName !== 'string') {
    return { isValid: false, error: '活動名稱不能為空' };
  }

  const trimmed = activityName.trim();
  if (trimmed.length === 0) {
    return { isValid: false, error: '活動名稱不能為空' };
  }

  if (trimmed.length > 200) {
    return { isValid: false, error: '活動名稱不能超過 200 字元' };
  }

  return { isValid: true, error: null };
}

/**
 * 准备活动基本信息
 * @param {string} title - 活动标题
 * @param {string} content - 活动内容
 * @param {string} chatmode - 聊天模式
 * @returns {Object} 活动基本信息对象
 */
export function prepareActivityInfo(title, content = '', chatmode = 'sticker') {
  return {
    title: (title || '').trim(),
    info: (content || '').trim(),
    chatmode: chatmode || 'sticker'
  };
}

/**
 * 准备节点群组数据
 * @param {Array} nodeGroups - 节点群组数组
 * @returns {Array} 清理和验证后的节点群组数组
 */
export function prepareNodeGroups(nodeGroups) {
  if (!Array.isArray(nodeGroups)) {
    return [];
  }

  return nodeGroups
    .filter(group => group && typeof group === 'object')
    .filter(group => Array.isArray(group.nodes) && group.nodes.length > 0)
    .map(group => ({
      id: group.id,
      name: group.name,
      description: group.description,
      timing: group.timing,
      isDefault: group.isDefault,
      nodes: group.nodes
        .filter(node => node && node.title && node.content)
        .map(node => ({
          id: node.id,
          title: node.title,
          content: node.content,
          tags: Array.isArray(node.tags) ? node.tags : []
        }))
    }))
    .filter(group => group.nodes.length > 0); // 再次过滤，移除没有有效节点的群组
}

/**
 * 准备夥伴数据
 * @param {Array} partners - 夥伴数组
 * @returns {Array} 清理后的夥伴数组
 */
export function preparePartners(partners) {
  if (!Array.isArray(partners)) {
    return [];
  }

  return partners
    .filter(partner => partner && typeof partner === 'object')
    .filter(partner => partner.name); // 至少要有名称
}

/**
 * 准备完整的活动创建数据
 * @param {Object} params - 参数对象
 * @param {number} params.userId - 用户 ID
 * @param {string} params.activityName - 活动名称
 * @param {Object} params.activityInfo - 活动信息
 * @param {Object} params.settings - 活动设置
 * @param {Array} params.nodeGroups - 节点群组
 * @param {Array} params.partners - AI 夥伴
 * @returns {Object} 完整的活动创建数据对象
 */
export function prepareActivityCreationData({
  userId,
  activityName,
  activityInfo,
  settings,
  nodeGroups,
  partners
}) {
  return {
    userId,
    activity: prepareActivityInfo(
      activityName,
      activityInfo?.content,
      settings?.chatmode
    ),
    nodeGroups: prepareNodeGroups(nodeGroups),
    selectedPartners: preparePartners(partners)
  };
}

/**
 * 验证活动创建数据的完整性
 * @param {Object} activityData - 活动数据对象
 * @returns {Object} { isValid: boolean, errors: Array<string> }
 */
export function validateActivityCreationData(activityData) {
  const errors = [];

  if (!activityData) {
    errors.push('活動數據不能為空');
    return { isValid: false, errors };
  }

  if (!activityData.userId) {
    errors.push('用戶 ID 不能為空');
  }

  if (!activityData.activity) {
    errors.push('活動基本信息不能為空');
  } else {
    const nameValidation = validateActivityName(activityData.activity.title);
    if (!nameValidation.isValid) {
      errors.push(nameValidation.error);
    }
  }

  if (!Array.isArray(activityData.nodeGroups)) {
    errors.push('節點群組必須是數組');
  }

  if (!Array.isArray(activityData.selectedPartners)) {
    errors.push('AI 夥伴必須是數組');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 计算活动统计信息
 * @param {Object} activityData - 活动数据对象
 * @returns {Object} 统计信息
 */
export function calculateActivityStats(activityData) {
  if (!activityData || typeof activityData !== 'object') {
    return {
      totalGroups: 0,
      totalNodes: 0,
      totalPartners: 0
    };
  }

  const nodeGroups = activityData.nodeGroups || [];
  const partners = activityData.selectedPartners || [];

  return {
    totalGroups: Array.isArray(nodeGroups) ? nodeGroups.length : 0,
    totalNodes: Array.isArray(nodeGroups) 
      ? nodeGroups.reduce((sum, group) => {
          return sum + (Array.isArray(group?.nodes) ? group.nodes.length : 0);
        }, 0)
      : 0,
    totalPartners: Array.isArray(partners) ? partners.length : 0
  };
}

/**
 * 格式化聊天模式标签
 * @param {string} chatmode - 聊天模式值
 * @returns {string} 格式化的标签文本
 */
export function formatChatModeLabel(chatmode) {
  const labels = {
    'sticker': 'Sticker 便利貼模式',
    'chatroom': 'Chatroom 聊天室模式',
    'tagged-forum': 'Tagged Forum 標籤論壇',
    'workspace': 'Workspace 工作區'
  };
  
  return labels[chatmode] || chatmode || '未設定';
}
